import type { Range } from '@livecontrol/store-utils';
import type { Draft } from 'immer';
import { produce } from 'immer';
import _ from 'lodash';
import type { State, StateCreator } from 'zustand';
import create from 'zustand';
import type { EventType, ScheduledEvent } from '../graphql';

// Immer middleware for zustand.
const immer =
  <T extends State>(config: StateCreator<T>): StateCreator<T> =>
  (set, get, api): T =>
    config(
      (partial, replace) => {
        const nextState =
          typeof partial === 'function' ? produce(<(state: Draft<T>) => T>partial) : <T>partial;

        set(nextState, replace);
      },
      get,
      api
    );

export interface EventFilters {
  eventType?: EventType;
  search?: string;
  dateRange?: Range<Date>;
  sortBy: 'cid' | 'eid' | 'organizationName' | 'startTime';
  sortOrder: 'ASC' | 'DESC';
  limit?: number;
  /** Selected validator (verifier_email) — used by the "My Events" tab only. */
  verifierEmail?: string;
}

interface PersistableState {
  filters: EventFilters;
  favoriteEvents: Set<ScheduledEvent['eid']>;
  eventConfig: Map<ScheduledEvent['eid'], { volume: number }>;
}

export interface EventsState extends PersistableState {
  toggleFavoriteEvent: (eventID: ScheduledEvent['eid']) => void;
  setFavoriteEvents: (eventIds: ScheduledEvent['eid'][]) => void;
  setEventType: (value: EventFilters['eventType']) => void;
  setDateRange: (value: EventFilters['dateRange']) => void;
  setSearch: (value: string) => void;
  setVerifierEmail: (value?: string) => void;
  setSort: (value: EventFilters['sortBy'], order: EventFilters['sortOrder']) => void;
  updateFilters: (value?: Partial<EventFilters>) => void;
  resetFilters: (value?: Partial<EventFilters>) => void;
  storeEventVolume: (eventID: ScheduledEvent['eid'], volume: number) => void;
}

// Persist middleware.
const PERSIST_KEY = '_events_state';

const persist =
  (config: StateCreator<EventsState>): StateCreator<EventsState> =>
  (set, get, api): EventsState =>
    config(
      (args) => {
        set(args);

        localStorage.setItem(
          PERSIST_KEY,
          JSON.stringify({
            filters: _.omit(get().filters, ['search', 'verifierEmail']),
            favoriteEvents: [...get().favoriteEvents],
            eventConfig: Object.fromEntries(get().eventConfig)
          })
        );
      },
      get,
      api
    );

/*
 * Pristine defaults. Kept separate from INITIAL_FILTERS (which merges whatever was
 * persisted to localStorage): resetFilters must clear a persisted eventType/dateRange,
 * so it has to reset to THIS clean baseline, not to the persisted-polluted INITIAL_FILTERS.
 */
const DEFAULT_FILTERS: EventFilters = {
  sortBy: 'startTime',
  sortOrder: 'ASC',
  limit: 20
};

const INITIAL_FILTERS: PersistableState = ((): PersistableState => {
  const defaultValue: PersistableState = {
    filters: { ...DEFAULT_FILTERS },
    favoriteEvents: new Set(),
    eventConfig: new Map()
  };

  const value = localStorage.getItem(PERSIST_KEY);

  if (value) {
    const json: Partial<PersistableState> = _.omit(
      <Partial<PersistableState>>JSON.parse(value),
      ['filters.search', 'filters.verifierEmail']
    );

    if (json.filters?.dateRange?.max) {
      json.filters.dateRange.max = new Date(json.filters.dateRange.max);
    }

    if (json.filters?.dateRange?.min) {
      json.filters.dateRange.min = new Date(json.filters.dateRange.min);
    }

    json.favoriteEvents = new Set(json.favoriteEvents);

    json.eventConfig = new Map(Object.entries(json.eventConfig ?? {}).map(([k, v]) => [k, v]));

    return { ...defaultValue, ...json };
  }

  return defaultValue;
})();

export const useEventsStore = create<EventsState>(
  persist(
    immer((set, get) => ({
      ...INITIAL_FILTERS,
      toggleFavoriteEvent: (eventID: ScheduledEvent['eid']): void => {
        set((v) => {
          if (v.favoriteEvents.has(eventID)) {
            v.favoriteEvents.delete(eventID);
            v.eventConfig.delete(eventID);
          } else {
            v.favoriteEvents.add(eventID);
          }
        });
      },
      setFavoriteEvents: (eventIDs: ScheduledEvent['eid'][]): void => {
        set((v) => {
          v.favoriteEvents.clear();
          v.favoriteEvents = new Set(eventIDs);

          // Cleanup event configs stored.
          for (const key of v.eventConfig.keys()) {
            if (!v.favoriteEvents.has(key)) {
              v.eventConfig.delete(key);
            }
          }
        });
      },
      storeEventVolume: (eventID: ScheduledEvent['eid'], volume: number): void => {
        set((v) => {
          const value = v.eventConfig.get(eventID);

          if (volume !== value?.volume) {
            v.eventConfig.set(eventID, { ...value, volume });
          }
        });
      },
      setEventType: (value): void => {
        set((v) => {
          v.filters.eventType = value;
        });
      },
      setDateRange: (value): void => {
        set((v) => {
          v.filters.dateRange = value;
        });
      },
      setSort: (value, order): void => {
        set((v) => {
          v.filters.sortBy = value;
          v.filters.sortOrder = order;
        });
      },
      setSearch: (value): void => {
        set((v) => {
          v.filters.search = value;
        });
      },
      setVerifierEmail: (value): void => {
        set((v) => {
          v.filters.verifierEmail = value;
        });
      },
      updateFilters: (updatedFilters?: Partial<EventFilters>): void => {
        set((v) => {
          const { filters } = get();

          Object.assign(v, { filters: { ...filters, ...updatedFilters } });
        });
      },
      resetFilters: (value?: Partial<EventFilters>): void => {
        set((v) => {
          Object.assign(v, { filters: { ...DEFAULT_FILTERS, ...value } });
        });
      }
    }))
  )
);
